import type { PreviewSceneImageState } from '@/features/preview/previewSceneBuilder'

export type PreviewImageGeometry = Pick<
  PreviewSceneImageState,
  'x' | 'y' | 'rotation' | 'rotationPivot' | 'rotationPoint' | 'isMirror'
> & {
  width: number
  height: number
}

export type PreviewPoint = {
  x: number
  y: number
}

export type PreviewQuad = [PreviewPoint, PreviewPoint, PreviewPoint, PreviewPoint]

export function getPreviewImagePivot(geometry: PreviewImageGeometry): PreviewPoint {
  if (geometry.rotationPivot === 'top-left') return { x: 0, y: 0 }
  if (geometry.rotationPivot === 'custom' && geometry.rotationPoint) {
    return {
      x: geometry.rotationPoint.x,
      y: geometry.rotationPoint.y,
    }
  }
  return {
    x: geometry.width / 2,
    y: geometry.height / 2,
  }
}

export function buildPreviewImageCorners(geometry: PreviewImageGeometry): PreviewQuad {
  const pivot = getPreviewImagePivot(geometry)
  const radians = (geometry.rotation * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const corners = [
    { x: 0, y: 0 },
    { x: geometry.width, y: 0 },
    { x: geometry.width, y: geometry.height },
    { x: 0, y: geometry.height },
  ]

  return corners.map((corner) => {
    const mirroredX = geometry.isMirror ? geometry.width - corner.x : corner.x
    const localX = mirroredX - pivot.x
    const localY = corner.y - pivot.y
    const rotatedX = localX * cos - localY * sin
    const rotatedY = localX * sin + localY * cos
    return {
      x: geometry.x + pivot.x + rotatedX,
      y: geometry.y + pivot.y + rotatedY,
    }
  }) as PreviewQuad
}

export function scalePreviewQuad(quad: PreviewQuad, scale: number): PreviewQuad {
  return quad.map((point) => ({
    x: point.x * scale,
    y: point.y * scale,
  })) as PreviewQuad
}

export function getPreviewImageBounds(quad: PreviewQuad) {
  const xs = quad.map((point) => point.x)
  const ys = quad.map((point) => point.y)
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...xs),
    bottom: Math.max(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  }
}

export function buildPreviewImageClipVertices(
  geometry: PreviewImageGeometry,
  scale: number,
  stageWidth: number,
  stageHeight: number,
): Float32Array {
  const quad = scalePreviewQuad(buildPreviewImageCorners(geometry), scale)
  const drawOrder = [quad[0], quad[1], quad[3], quad[2]]
  const values: number[] = []
  for (const point of drawOrder) {
    values.push((point.x / stageWidth) * 2 - 1)
    values.push(1 - (point.y / stageHeight) * 2)
  }
  return new Float32Array(values)
}

export function isPointInsidePreviewQuad(point: PreviewPoint, quad: PreviewQuad): boolean {
  let sign = 0
  for (let index = 0; index < quad.length; index += 1) {
    const current = quad[index]
    const next = quad[(index + 1) % quad.length]
    const cross = (next.x - current.x) * (point.y - current.y) - (next.y - current.y) * (point.x - current.x)
    if (Math.abs(cross) < 0.001) continue
    const nextSign = cross > 0 ? 1 : -1
    if (sign === 0) {
      sign = nextSign
      continue
    }
    if (sign !== nextSign) return false
  }
  return true
}

export function toScreenPoint(point: PreviewPoint, scale: number): PreviewPoint {
  return {
    x: point.x * scale,
    y: point.y * scale,
  }
}

export function toDesignPoint(point: PreviewPoint, scale: number): PreviewPoint {
  const safeScale = Math.max(0.0001, scale)
  return {
    x: point.x / safeScale,
    y: point.y / safeScale,
  }
}

export function clampPreviewPoint(point: PreviewPoint, width: number, height: number): PreviewPoint {
  return {
    x: Math.max(0, Math.min(width, point.x)),
    y: Math.max(0, Math.min(height, point.y)),
  }
}

export function normalizeDegrees(value: number): number {
  const next = Number.isFinite(value) ? value : 0
  const normalized = ((next % 360) + 360) % 360
  return normalized > 180 ? normalized - 360 : normalized
}
